// ============================================================================
// DYNAMIC ALGORITHM REGISTRY (CLEAN ARCHITECTURE)
// ============================================================================
import { BaseGenerator } from '../generators/base-generator';

export interface AlgorithmMeta {
  id: string;
  name: string;
  category: string;
  version?: string;
  complexity: {
    time: string;
    space: string;
  };
  description: string;
  load: () => Promise<BaseGenerator<unknown> | null>;
}

export interface AlgorithmInfo extends Omit<AlgorithmMeta, 'load'> {
  path: string;
}

export interface AlgorithmCategory {
  name: string;
  algorithms: AlgorithmMeta[];
}

// Загрузка метаданных строго через относительный путь
const metaModules = import.meta.glob('../../algorithms/**/*.meta.ts', { eager: true }) as Record<
  string,
  { meta: Omit<AlgorithmMeta, 'load'> }
>;

// Ленивая загрузка генераторов
const generatorModules = import.meta.glob('../../algorithms/**/*.generator.ts') as Record<
  string,
  () => Promise<Record<string, unknown>>
>;

function parseMetaPath(modulePath: string): { category: string; algorithmId: string; path: string } | null {
  if (!modulePath.endsWith('.meta.ts')) return null;

  // Capture category (first segment) and full sub-path (everything between category and .meta.ts)
  // e.g. "algorithms/graph/bfs/bfs-dp-hybrid.meta.ts" → category="graph", fullPath="bfs/bfs-dp-hybrid"
  const match = modulePath.match(/algorithms\/([^/]+)\/(.+)\.meta\.ts$/);
  if (!match) return null;

  const [, category, fullPath] = match;
  if (!category || !fullPath) return null;

  // algorithmId = last segment of fullPath (the filename without extension)
  const segments = fullPath.split('/');
  const algorithmId = segments[segments.length - 1]!;

  // Форматируем красивое имя категории (например: data-structures -> Data Structures, dp -> DP)
  const formattedCategory = category
    .split('-')
    .map(word => {
      const lower = word.toLowerCase();
      if (lower === 'dp') return 'DP';
      if (lower === 'sat') return 'SAT';
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');

  return {
    category: formattedCategory,
    algorithmId,
    path: `${category}/${fullPath}`,
  };
}

export async function loadGenerator(algorithmPath: string): Promise<BaseGenerator<unknown> | null> {
  if (!algorithmPath) return null;

  // Use exact match: path + .generator.ts suffix prevents matching sibling generators
  // e.g. "graph/bfs/bfs" matches "algorithms/graph/bfs/bfs.generator.ts" but NOT "bfs-dp-hybrid.generator.ts"
  const targetKey = Object.keys(generatorModules).find(
    key => key.includes(`algorithms/${algorithmPath}.generator.ts`)
  );

  try {
    if (!targetKey || !generatorModules[targetKey]) return null;

    const module = await generatorModules[targetKey]();
    if (!module) return null;

    const isGeneratorClass = (value: unknown): value is new () => BaseGenerator<unknown> =>
      typeof value === 'function' && (value as { prototype?: { generate?: unknown } }).prototype?.generate !== undefined;

    const defaultExport = module.default;
    const GeneratorClass = defaultExport && isGeneratorClass(defaultExport)
      ? defaultExport
      : Object.values(module).find(isGeneratorClass);

    return GeneratorClass ? new GeneratorClass() : null;
  } catch (error) {
    console.error(`[AlgorithmRegistry] Lazy load failed for ${algorithmPath}:`, error);
    return null;
  }
}

function loadAllMetadataWithPath(): AlgorithmInfo[] {
  const algorithms: AlgorithmInfo[] = [];
  const seenIds = new Set<string>();
  const skipped: string[] = [];

  for (const modulePath in metaModules) {
    if (!modulePath.endsWith('.meta.ts')) continue;

    const parsed = parseMetaPath(modulePath);
    const meta = metaModules[modulePath]?.meta;

    if (parsed && meta) {
      const id = meta.id || parsed.algorithmId;

      // Deduplicate by ID — first occurrence wins
      if (seenIds.has(id)) {
        console.warn(`[AlgorithmRegistry] Duplicate algorithm ID "${id}" at ${modulePath} — skipping`);
        skipped.push(`${modulePath} (dup id: ${id})`);
        continue;
      }
      seenIds.add(id);

      algorithms.push({
        id,
        name: meta.name || parsed.algorithmId,
        category: parsed.category,
        complexity: meta.complexity || { time: 'N/A', space: 'N/A' },
        description: meta.description || '',
        path: parsed.path,
      });
    } else {
      skipped.push(`${modulePath} (parsed=${!!parsed}, meta=${!!meta})`);
    }
  }

  console.log(`[AlgorithmRegistry] metaModules keys: ${Object.keys(metaModules).length}, algorithms loaded: ${algorithms.length}, skipped: ${skipped.length}`);
  if (skipped.length > 0) {
    console.log('[AlgorithmRegistry] Skipped modules:', skipped);
  }

  if (algorithms.length === 0) {
    return [{ id: 'quick-sort', name: 'Quick Sort', category: 'Sorting', complexity: { time: 'O(n log n)', space: 'O(log n)' }, description: '', path: 'sorting/quick-sort' }];
  }
  return algorithms;
}

function groupByCategory(algorithms: AlgorithmMeta[]): AlgorithmCategory[] {
  const categoryMap = new Map<string, AlgorithmMeta[]>();
  for (const algo of algorithms) {
    if (!categoryMap.has(algo.category)) categoryMap.set(algo.category, []);
    categoryMap.get(algo.category)!.push(algo);
  }
  return Array.from(categoryMap.entries()).map(([name, algos]) => ({
    name,
    algorithms: algos.sort((a, b) => a.name.localeCompare(b.name)),
  })).sort((a, b) => a.name.localeCompare(b.name));
}

// Single source of truth — derive AlgorithmMeta[] from AlgorithmInfo[]
const _allAlgorithms = loadAllMetadataWithPath();

export const availableAlgorithms: AlgorithmInfo[] = _allAlgorithms;
export const algorithmRegistry: AlgorithmMeta[] = _allAlgorithms.map(a => ({
  ...a,
  load: () => loadGenerator(a.path),
}));
export const ALGORITHM_CATEGORIES: AlgorithmCategory[] = groupByCategory(algorithmRegistry);

export function getAlgorithmById(id: string): AlgorithmMeta | null {
  return algorithmRegistry.find(algo => algo.id === id) || null;
}